import React from "react";
import TopLeft from "./TopLeft";
import _ from "underscore";
import { TimeEdit } from "../../lib/TimeEdit";
import ErrorBoundary from "../ErrorBoundary";
import coreAPI from "./ExamCoreAPI";
import { Macros } from "../../models/Macros";
import { Reservation } from "../../models/Reservation";
import API from "../../lib/TimeEditAPI";
import Language from "../../lib/Language";
import Log from "../../lib/Log";
// eslint-disable-next-line import/no-unresolved
import examPluginCss from "@timeedit/te-exam-plugin/dist/exam-plugin.css?inline";
import { TAppContextProp, withAppContext } from "../../contexts/AppContext";
import LazyLoadWrapper from "../LazyLoadWrapper";

/** An exam request, as the plugin hands it over through the macro. */
type TExamRequest = {
    objects: { type: { extid: string } }[];
    fields?: { id: number; values: string[] }[];
    mode?: string;
};

type TExamWrapperProps = {
    classes?: Record<string, boolean>;
    style?: React.CSSProperties;
    topLeft?: React.JSX.Element;
    /** Opens the reservation info for a reservation picked in the plugin. */
    onInfoOpen?: (ids: number[], showPanel?: boolean, editMode?: boolean) => void;
} & TAppContextProp;

type TExamWrapperState = {
    toolbar: React.JSX.Element | null;
    env: string;
    /** One request, or a list of them; the other is null. */
    examRequest: TExamRequest | null;
    examRequests: TExamRequest[] | null;
    examId: string | null;
    examIds: (string | undefined)[] | null;
};

const TEExamPlugin = React.lazy(() => import("@timeedit/te-exam-plugin"));

class ExamWrapper extends React.Component<TExamWrapperProps, TExamWrapperState> {
    state: TExamWrapperState = {
        toolbar: null,
        env: this.getEnv(),
        examRequest: null,
        examId: null,
        examRequests: null,
        examIds: null,
    };

    getEnv() {
        return this.props.appContext.env.serverEnv;
    }

    shouldComponentUpdate(nextProps, nextState) {
        return (
            this.state.toolbar !== nextState.toolbar ||
            !_.isEqual(this.state.examRequest, nextState.examRequest) ||
            !_.isEqual(this.state.examRequests, nextState.examRequests) ||
            !_.isEqual(this.state.env, nextState.env)
        );
    }

    componentDidMount() {
        if (process.env.NODE_ENV === "development") {
            TimeEdit.examAPI = coreAPI(
                this.props,
                this.props.appContext.user,
                this.setToolbar.bind(this)
            );
        }
        // eslint-disable-next-line no-console
        console.log(`Core Exam plugin environment: ${this.state.env}`);
        this.registerMacros();
        TimeEdit.onCreateExam = this.onCreateExam;
        this.props.appContext.registerExamAPI(
            coreAPI(this.props, this.props.appContext.user, this.setToolbar.bind(this))
        );
    }
    componentWillUnmount() {
        this.props.appContext.deregisterMacro("examComponent");
        this.props.appContext.registerExamAPI(null);
    }
    registerMacros() {
        this.props.appContext.registerMacro("examComponent", {
            events: [Macros.Event.EXAM_REQUEST_UPDATE],
            actions: [
                {
                    key: Macros.Action.UPDATE_EXAM_REQUEST,
                    action: (newRequest) => {
                        if (Array.isArray(newRequest)) {
                            const mappedRequests = newRequest.map((request) =>
                                Object.assign({}, request, {
                                    objects: request.objects.map((obj) =>
                                        Object.assign({}, obj, { typeextid: obj.type.extid })
                                    ),
                                })
                            );
                            // eslint-disable-next-line no-console
                            console.log(mappedRequests);
                            const examIds = this.props.appContext.examIdField
                                ? mappedRequests.map(
                                      (request) =>
                                          _.find(
                                              request.fields,
                                              (field) =>
                                                  field.id === this.props.appContext.examIdField
                                          )?.values[0]
                                  )
                                : null;
                            this.setState({
                                examRequest: null,
                                examRequests: mappedRequests,
                                examIds,
                                examId: null,
                            });
                        } else {
                            const mappedRequest = Object.assign({}, newRequest, {
                                objects: newRequest.objects.map((obj) =>
                                    Object.assign({}, obj, { typeextid: obj.type.extid })
                                ),
                            });
                            // eslint-disable-next-line no-console
                            console.log(mappedRequest);
                            const examId = this.props.appContext.examIdField
                                ? _.find(
                                      newRequest.fields,
                                      (field) => field.id === this.props.appContext.examIdField
                                  )?.values[0]
                                : null;
                            this.setState({
                                examRequest: mappedRequest,
                                examRequests: null,
                                examId,
                                examIds: null,
                            });
                        }
                    },
                },
            ],
        });
    }
    setToolbar(toolbar) {
        this.setState({ toolbar });
    }
    selectReservation(reservationId) {
        Reservation.get([reservationId], (reservations) => {
            if (reservations.length === 0 || reservations[0] === null) {
                const details = Language.get(
                    "nc_error_no_reservation_found_with_id",
                    reservationId
                );
                Log.error(details);
                return;
            }
            try {
                this.props.appContext.fireEvent(
                    `examInCore`,
                    Macros.Event.SELECT_RESERVATION,
                    reservations[0]
                );
                this.props.onInfoOpen?.([reservationId], false);
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error(error);
            }
        });
    }
    // TODO How to handle multiple reservations being created?
    onCreateExam(examId, reservationId) {
        API.setModifiableReservationFields(
            [reservationId],
            [
                {
                    class: "fieldvalue",
                    id: this.props.appContext.examIdField!,
                    values: [String(examId)],
                },
            ],
            // eslint-disable-next-line no-unused-vars
            (result) => {
                this.props.appContext.fireEvent(
                    `examInCore`,
                    Macros.Event.RESERVATION_MADE_OR_MODIFIED,
                    [reservationId],
                    false,
                    true
                );
            }
        );
    }

    // TODO How to handle multiple reservations being created?
    onReservationUpdated(reservationId) {
        this.props.appContext.fireEvent(
            `examInCore`,
            Macros.Event.RESERVATION_MADE_OR_MODIFIED,
            [reservationId],
            false,
            true
        );
    }

    // TODO DOes this need to handle multiple reservations? If so: how?
    onEditExam(reservationId) {
        // eslint-disable-next-line no-console
        console.log(reservationId);
        // Invoke editing in the same way as from Entry.jsx, line 558
        if (reservationId) {
            API.getReservationsByExtid([reservationId], (result) => {
                if (result.length > 0) {
                    this.props.appContext.createExamRequest(result[0]);
                } else {
                    this.props.appContext.clearExamRequest();
                    this.setState({ examId: null, examRequest: null });
                }
            });
        } else {
            this.props.appContext.clearExamRequest();
            this.setState({ examId: null, examRequest: null });
        }
    }

    render() {
        const examAPI = coreAPI(this.props, this.props.appContext.user, this.setToolbar.bind(this));
        let env = this.state.env;
        let reservations = this.state.examRequest
            ? [this.state.examRequest]
            : this.state.examRequests;
        if (reservations === null) {
            reservations = [];
        }
        let mode = "ALL";
        if (reservations.length > 0 && reservations[0].mode) {
            mode = reservations[0].mode;
        }
        return (
            <div className={_.classSet(this.props.classes)} style={this.props.style}>
                <TopLeft topLeft={this.props.topLeft} contents={this.state.toolbar} />
                <ErrorBoundary>
                    <div style={{ flex: "1", overflow: "auto" }}>
                        <LazyLoadWrapper>
                            <TEExamPlugin
                                mixpanel={{ track: () => {} }}
                                {...this.props}
                                usingSSO={true}
                                platformVersion={"3"}
                                region={this.props.appContext.env.serverRegion}
                                env={env}
                                requestData={{ reservations, mode }}
                                showInCalendar={this.selectReservation.bind(this)}
                                onCreateExam={this.onCreateExam.bind(this)}
                                onEditExam={this.onEditExam.bind(this)}
                                onReservationUpdated={this.onReservationUpdated.bind(this)}
                                coreAPI={examAPI}
                                setToolbar={this.setToolbar.bind(this)}
                            />
                        </LazyLoadWrapper>
                        <style type="text/css">{examPluginCss}</style>
                    </div>
                </ErrorBoundary>
            </div>
        );
    }
}

export default withAppContext(ExamWrapper);
